import { useEffect, useState, type ComponentType } from "react";
import {
  Check,
  ChevronDown,
  Download,
  Loader2,
  Maximize2,
  Minimize2,
  Plus,
  RotateCcw,
  Trash2,
  X,
  type LucideProps,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import type { PanelVersion } from "@/hooks/use-report-panel";

export interface PdfAction {
  label: string;
  onClick: () => void;
  icon?: ComponentType<LucideProps>;
}

/**
 * A prominent right-side action button registered by the mounted report
 * component (e.g. Valuations' "Generate Report"). Lets a self-contained
 * report surface its own primary action in the shared toolbar instead of
 * rendering it inline in the report body.
 */
export interface ToolbarPrimaryAction {
  label: string;
  loadingLabel?: string;
  onClick: () => void;
  isLoading?: boolean;
  disabled?: boolean;
  icon?: ComponentType<LucideProps>;
}

/**
 * A compact toggle/secondary button registered alongside the primary action
 * (e.g. Valuations' Pause Live / Reset). `active` renders the highlighted
 * "on" state.
 */
export interface ToolbarSecondaryAction {
  key: string;
  label: string;
  onClick: () => void;
  icon?: ComponentType<LucideProps>;
  active?: boolean;
  disabled?: boolean;
  title?: string;
}

/** Combined toolbar contribution a panel-mode component can register. */
export interface PanelToolbarActions {
  primaryAction?: ToolbarPrimaryAction;
  secondaryActions?: ToolbarSecondaryAction[];
}

function fullscreenShortcutLabel(): string {
  const nav = typeof navigator !== "undefined" ? navigator : null;
  const isMac = !!nav && /Mac|iPhone|iPad|iPod/i.test(nav.platform || nav.userAgent || "");
  return isMac ? "⌘ \\" : "Ctrl + \\";
}

interface ReportPanelToolbarProps {
  title: string;
  versions: PanelVersion[];
  selectedVersionId: string;
  onVersionChange: (id: string) => void;
  /** Hide version dropdown entirely — valuations / deal-memo. */
  hideVersionSelector?: boolean;
  /** Hide the Generate New button — deal-memo. */
  hideGenerate?: boolean;
  /** Hide the Delete button — valuations / deal-memo. */
  hideDelete?: boolean;
  canGenerate: boolean;
  canDelete: boolean;
  isGenerating: boolean;
  isDeleting: boolean;
  onGenerateNew: () => void;
  onDelete: () => void;
  /**
   * PDF export actions registered by the mounted report component. Empty/
   * undefined hides the button; a single action renders a single PDF button;
   * two or more render a dropdown.
   */
  pdfActions?: PdfAction[];
  /**
   * Optional prominent action registered by the mounted component, shown in
   * the right-hand button group. Used by self-contained reports (valuations)
   * to surface their own Generate button in the toolbar.
   */
  primaryAction?: ToolbarPrimaryAction;
  /** Compact toggle/secondary buttons registered by the mounted component. */
  secondaryActions?: ToolbarSecondaryAction[];
  /** Maximize/minimize is optional; omit to hide the toggle. */
  maximized?: boolean;
  onToggleMaximize?: () => void;
  onClose: () => void;
  /** Failure banner state. */
  hasError?: boolean;
  onRetry?: () => void;
  onDismissError?: () => void;
}

export function ReportPanelToolbar({
  title,
  versions,
  selectedVersionId,
  onVersionChange,
  hideVersionSelector,
  hideGenerate,
  hideDelete,
  canGenerate,
  canDelete,
  isGenerating,
  isDeleting,
  onGenerateNew,
  onDelete,
  pdfActions,
  primaryAction,
  secondaryActions,
  maximized,
  onToggleMaximize,
  onClose,
  hasError,
  onRetry,
  onDismissError,
}: ReportPanelToolbarProps) {
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const PrimaryActionIcon = primaryAction?.icon ?? Plus;

  const selected = versions.find((v) => v.id === selectedVersionId) ?? versions[0];
  const activeNumber = selected?.versionNumber;
  const generatingVersion = versions.find((v) => v.generating);

  // A pending "confirm" is only valid for the currently-selected version and
  // only while deletion is actually allowed. Switching versions, or losing
  // `canDelete` mid-confirm (e.g. a generation starts), must require a fresh
  // first click so the red "Confirm" state can't linger invisibly and fire
  // when the button re-enables.
  useEffect(() => {
    setConfirmingDelete(false);
  }, [selectedVersionId, canDelete]);

  const handleDeleteClick = () => {
    if (!confirmingDelete) {
      setConfirmingDelete(true);
      return;
    }
    setConfirmingDelete(false);
    onDelete();
  };

  return (
    <>
      {/* Toolbar row */}
      <div className="flex items-center justify-between border-b border-border bg-card px-4 py-2.5 shrink-0">
        <div className="flex items-center gap-1.5">
          {!hideVersionSelector && versions.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="flex h-7 items-center gap-1 rounded-md border border-border px-2.5 text-xs text-muted-foreground transition-colors hover:bg-foreground/[0.04] hover:text-foreground"
                  title="Pick report version"
                >
                  <span className="font-mono">v{activeNumber ?? versions.length}</span>
                  <ChevronDown className="h-3 w-3 opacity-60" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="min-w-[220px]">
                <DropdownMenuLabel className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  Generations
                </DropdownMenuLabel>
                {versions.map((v) => {
                  const isActive = v.id === selectedVersionId;
                  return (
                    <DropdownMenuItem
                      key={v.id}
                      onClick={() => onVersionChange(v.id)}
                      className={cn(
                        "flex items-center gap-2",
                        isActive && "bg-accent/50"
                      )}
                    >
                      <Check
                        className={cn(
                          "h-3.5 w-3.5 shrink-0",
                          isActive ? "opacity-100" : "opacity-0"
                        )}
                      />
                      <span className="font-mono text-xs">v{v.versionNumber}</span>
                      <span className="flex-1 truncate text-xs text-muted-foreground">
                        {v.generatedAt}
                      </span>
                      {v.generating && (
                        <Loader2 className="h-3 w-3 shrink-0 animate-spin text-blue-600 dark:text-blue-400" />
                      )}
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {/*
            Enablement is the registering component's call, not the
            toolbar's: it only registers actions once it has something
            renderable. Gating on `selectedVersionId` here used to
            permanently disable the button for the version-less reports
            (deal-memo, valuations, questionnaire), which have no id to
            select. `isGenerating` stays, so a mid-generation click can't
            export a half-written report.
          */}
          {pdfActions && pdfActions.length === 1 && (
            <button
              type="button"
              onClick={pdfActions[0].onClick}
              disabled={isGenerating}
              className="flex h-7 items-center gap-1.5 rounded-md border border-border px-2.5 text-xs text-muted-foreground transition-colors hover:bg-foreground/[0.04] hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40"
              title={pdfActions[0].label}
            >
              <Download className="h-3 w-3" />
              PDF
            </button>
          )}
          {pdfActions && pdfActions.length > 1 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  disabled={isGenerating}
                  className="flex h-7 items-center gap-1.5 rounded-md border border-border px-2.5 text-xs text-muted-foreground transition-colors hover:bg-foreground/[0.04] hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40"
                  title="Download PDF"
                >
                  <Download className="h-3 w-3" />
                  PDF
                  <ChevronDown className="h-3 w-3 opacity-60" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="min-w-[220px]">
                {pdfActions.map((action) => {
                  const Icon = action.icon ?? Download;
                  return (
                    <DropdownMenuItem
                      key={action.label}
                      onClick={action.onClick}
                      className="flex items-center gap-2"
                    >
                      <Icon className="h-3.5 w-3.5 shrink-0" />
                      <span className="text-xs">{action.label}</span>
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>

        <div className="flex items-center gap-1.5">
          {!hideDelete && (
            <button
              type="button"
              onClick={handleDeleteClick}
              onMouseLeave={() => setConfirmingDelete(false)}
              disabled={!canDelete}
              title={
                confirmingDelete
                  ? "Click again to confirm"
                  : selected
                  ? `Delete v${activeNumber}`
                  : "Delete report"
              }
              className={cn(
                "flex h-7 items-center gap-1.5 rounded-md px-2 transition-colors disabled:cursor-not-allowed disabled:opacity-40",
                confirmingDelete
                  ? "bg-destructive/10 text-destructive"
                  : "text-muted-foreground/70 hover:bg-destructive/10 hover:text-destructive"
              )}
            >
              {isDeleting ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Trash2 className="h-3.5 w-3.5" />
              )}
              {confirmingDelete && !isDeleting && (
                <span className="text-[11px] font-medium">Confirm</span>
              )}
            </button>
          )}

          {!hideGenerate && (
            <button
              type="button"
              onClick={onGenerateNew}
              disabled={!canGenerate}
              className="flex h-7 items-center gap-1.5 rounded-md bg-foreground px-3 text-xs font-medium text-background transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isGenerating ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <Plus className="h-3 w-3" />
              )}
              {isGenerating ? "Generating…" : "New version"}
            </button>
          )}

          {secondaryActions?.map((action) => {
            const Icon = action.icon;
            return (
              <button
                key={action.key}
                type="button"
                onClick={action.onClick}
                disabled={action.disabled}
                title={action.title ?? action.label}
                className={cn(
                  "flex h-7 items-center gap-1.5 rounded-md border px-2.5 text-xs transition-colors disabled:cursor-not-allowed disabled:opacity-40",
                  action.active
                    ? "border-primary/40 bg-primary/10 text-primary"
                    : "border-border text-muted-foreground hover:bg-foreground/[0.04] hover:text-foreground"
                )}
              >
                {Icon && <Icon className="h-3 w-3" />}
                {action.label}
              </button>
            );
          })}

          {primaryAction && (
            <button
              type="button"
              onClick={primaryAction.onClick}
              disabled={primaryAction.disabled || primaryAction.isLoading}
              className="flex h-7 items-center gap-1.5 rounded-md bg-foreground px-3 text-xs font-medium text-background transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {primaryAction.isLoading ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <PrimaryActionIcon className="h-3 w-3" />
              )}
              {primaryAction.isLoading
                ? primaryAction.loadingLabel ?? "Working…"
                : primaryAction.label}
            </button>
          )}

          {onToggleMaximize && (
            <TooltipProvider delayDuration={200}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={onToggleMaximize}
                    aria-label={
                      maximized ? "Exit fullscreen" : "Enter fullscreen"
                    }
                    className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                  >
                    {maximized ? (
                      <Minimize2 className="h-3.5 w-3.5" />
                    ) : (
                      <Maximize2 className="h-3.5 w-3.5" />
                    )}
                  </button>
                </TooltipTrigger>
                <TooltipContent
                  side="bottom"
                  className="flex items-center gap-1.5 bg-black px-2 py-1 text-[11px] text-white"
                >
                  {maximized ? "Exit fullscreen" : "Fullscreen"}
                  <kbd className="font-mono text-[10px] opacity-70">
                    {fullscreenShortcutLabel()}
                  </kbd>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          <button
            type="button"
            onClick={onClose}
            aria-label="Close panel"
            className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {/* Generating banner */}
      {isGenerating && !hasError && (
        <div className="flex shrink-0 items-center gap-2 border-b border-border bg-blue-50 px-4 py-2 text-[11px] text-blue-700 dark:bg-blue-950/40 dark:text-blue-300">
          <Loader2 className="h-3 w-3 animate-spin" />
          <span className="font-medium">
            {/* Only name a version number for an actual new-generation run
                (which carries an optimistic placeholder). A refine adds a
                refinement to the current generation, so fabricating the next
                generation number here would be wrong — stay neutral. */}
            {generatingVersion
              ? `Generating v${generatingVersion.versionNumber} of ${title}`
              : `Generating ${title}…`}
          </span>
          <span className="text-muted-foreground/70">· usually takes a few seconds</span>
        </div>
      )}

      {/* Failure banner */}
      {hasError && !isGenerating && (
        <div className="flex shrink-0 items-center gap-2 border-b border-border bg-destructive/10 px-4 py-2 text-[11px] text-destructive">
          <span className="font-medium">Generation failed for {title}</span>
          {onRetry && (
            <button
              type="button"
              onClick={onRetry}
              className="ml-auto inline-flex items-center gap-1 rounded border border-destructive/40 bg-background px-2 py-0.5 font-medium text-destructive transition-colors hover:bg-destructive/10"
            >
              <RotateCcw className="h-3 w-3" />
              Retry
            </button>
          )}
          {onDismissError && (
            <button
              type="button"
              onClick={onDismissError}
              aria-label="Dismiss error"
              className="flex h-5 w-5 items-center justify-center rounded text-destructive hover:bg-destructive/10"
            >
              <X className="h-3 w-3" />
            </button>
          )}
        </div>
      )}
    </>
  );
}
